import { resolvePlanTierKey, type PlanTierSource } from 'constants/planComparison'

/**
 * The minimum a plan must carry for the annual saving to be computed from it.
 * Structural rather than a concrete plan type for the same reason
 * `PlanColumnPlan` is: two catalogs feed this — the authenticated
 * `GET /subscriptions/get-all-plans` (`SubscriptionPlan`) and the public
 * `GET /signup/plans` (adapted to snake_case at the signup call site).
 */
export interface PlanIntervalSavingsPlan extends PlanTierSource {
  /** Stripe `unit_amount`-shaped — always cents. */
  amount: number
  interval: string
}

/**
 * How far apart the tiers' savings may sit, in percentage points, before a
 * single approximate figure stops being an honest summary of all of them.
 *
 * At the current catalog the four tiers span 0.76 points (15.91% to 16.67%), so
 * one number describes every column to within a point. If a future price change
 * spreads them wider than this, the label degrades to "up to N%" by itself —
 * which is the guard that matters, because the failure being fixed here is
 * exactly a claim that stopped tracking the prices under it.
 */
const SINGLE_FIGURE_SPREAD_LIMIT = 1

/**
 * Interval matching, kept character-identical to `matchesInterval` on the
 * billing plan-change page and `matchesPlanInterval` on the signup page. If the
 * catalog ever grows a spelling those two do not accept, this must NOT be the
 * place it is first accepted: a plan the matrix filters out but this function
 * counts would put a saving on the chip for a column nobody can see.
 */
const isAnnual = (interval: string): boolean => {
  const value = (interval || '').toString().toLowerCase()
  return value === 'annual' || value === 'annually' || value === 'year'
}

/**
 * EXPORTED for the signups grid's potential-revenue column (VRG-545), which prices a lead
 * against the MONTHLY catalog and must accept exactly the spellings this file and the two
 * pages named above accept. It was the fourth place about to hand-roll this three-line
 * predicate; §36 says the second is already one too many, and the failure mode of a fourth
 * copy is specific — a spelling one copy accepts and another does not produces a plan the
 * matrix hides but the revenue column prices, i.e. a figure computed from a plan nobody can
 * buy. Kept in this file rather than hoisted to a util because the comment above it (the
 * "must NOT be the place a new spelling is first accepted" rule) is the thing that matters
 * and it belongs with the pair.
 */
export const isMonthly = (interval: string): boolean => {
  const value = (interval || '').toString().toLowerCase()
  return value === 'month' || value === 'monthly'
}

/** Cheapest amount per tier for one interval — a defensive `Math.min` in case
 *  the catalog ever carries two live prices for the same tier and interval. */
const cheapestByTier = (
  plans: PlanIntervalSavingsPlan[],
  matches: (interval: string) => boolean,
): Map<string, number> => {
  const byTier = new Map<string, number>()
  for (const plan of plans) {
    if (!matches(plan.interval)) continue
    const tier = resolvePlanTierKey(plan)
    if (!tier || !Number.isFinite(plan.amount) || plan.amount <= 0) continue
    const existing = byTier.get(tier)
    byTier.set(tier, existing === undefined ? plan.amount : Math.min(existing, plan.amount))
  }
  return byTier
}

/**
 * What each tier saves by paying annually, as an EXACT percentage of twelve
 * monthly payments (unrounded — rounding is the label's decision, and a caller
 * comparing tiers wants the real spread). One entry per tier that has both
 * intervals in the catalog and where annual is actually cheaper.
 *
 * Measured 2026-08-26: starter 16.67%, individual 15.91%, business 16.42%,
 * scale 16.59%.
 */
export const planIntervalSavingsByTier = (
  plans: PlanIntervalSavingsPlan[],
): Map<string, number> => {
  const monthly = cheapestByTier(plans, isMonthly)
  const annual = cheapestByTier(plans, isAnnual)

  const savings = new Map<string, number>()
  for (const [tier, monthlyAmount] of monthly) {
    const annualAmount = annual.get(tier)
    if (annualAmount === undefined) continue
    const payingMonthly = monthlyAmount * 12
    if (annualAmount >= payingMonthly) continue
    savings.set(tier, ((payingMonthly - annualAmount) / payingMonthly) * 100)
  }
  return savings
}

/**
 * The "Save N%" pill's text, DERIVED from the live catalog rather than written
 * down. Returns `null` when it cannot be derived — while plans are loading, or
 * if a catalog ever ships without a monthly/annual pair — because a missing
 * pill is a non-claim and a stale one is a false claim.
 *
 * WHY THIS IS COMPUTED AT ALL. The chip was the literal `'Save 19%'` from
 * VERA-644 until 2026-08-26. No tier has ever saved 19%: the four save 16.67% /
 * 15.91% / 16.42% / 16.59%, so the chip overstated the discount by ~2.5 points
 * on every plan, on the public signup page as well as the authenticated billing
 * page. A corrected literal would only reset the same clock — the number is a
 * function of the prices, so it is read from them.
 *
 * ROUNDING IS FLOORED, NOT NEAREST, and that is the whole argument. A price
 * claim's errors are not symmetric: a figure the customer beats is a pleasant
 * surprise, one they miss is a misrepresentation. Nearest-rounding produces
 * "~17%" here (starter 16.67 and scale 16.59 both round up), and 17 is above
 * ALL FOUR tiers — the same class of error as the 19, just smaller. Flooring
 * the mean gives 16, which three of the four tiers beat.
 *
 * Stated exactly, because "understates every tier" would be too strong: 16 is
 * beaten by starter, business and scale, and sits 0.09 points above individual
 * (15.909%). The `~` carries that tenth of a point. Reaching a figure no tier
 * could ever fall short of would mean "~15%", a full point under the plan most
 * prospects land on, which trades a real understatement for a rounding artefact.
 *
 * "N months free" was considered and rejected as the wording. It is exact only
 * on Starter (290/29 = 10.00 months paid); Individual is 10.09, Business 10.03
 * and Scale 10.01, so "2 months free" overstates three of four tiers — the same
 * defect being fixed, in a different unit.
 */
export const planIntervalSavingsLabel = (
  plans: PlanIntervalSavingsPlan[],
): string | null => {
  const savings = [...planIntervalSavingsByTier(plans).values()].filter(
    (percent) => percent > 0,
  )
  if (savings.length === 0) return null

  const lowest = Math.min(...savings)
  const highest = Math.max(...savings)

  // Tiers too far apart for one number to speak for all of them: fall back to
  // the only claim that is then literally true of every column.
  if (highest - lowest > SINGLE_FIGURE_SPREAD_LIMIT) {
    return `Save up to ${Math.floor(highest)}%`
  }

  const mean = savings.reduce((total, percent) => total + percent, 0) / savings.length
  const figure = Math.floor(mean)
  if (figure < 1) return null

  // No `~` when every tier lands on the figure exactly — approximating an exact
  // number reads as hedging.
  const exact = savings.every((percent) => percent === figure)
  return exact ? `Save ${figure}%` : `Save ~${figure}%`
}
